/**
 * Macrokit Studio (preview) — browser controller.
 *
 * Fully client-only, keyless, zero-backend. The macros are pre-authored at
 * design time and bundled; the browser runs only the SDK runtime, with a REAL
 * in-browser model (WebLLM / WebGPU) routing intents to those macros. GitHub
 * public reads go out via fetch. Nothing is sent to any Macrokit server.
 */
import { handleTask, type Repo } from "./agent.js";
import { AUTHORED_MACROS } from "./authored-macros.js";
import type { RouteEnv } from "./env.js";
import { GitHubClient } from "./github.js";
import { Session, authorMacro, loadAuthoredMacros, type RequestRecord } from "./session.js";
import { WebLLMAdapter } from "./webllm-adapter.js";

// Tiny model is the default: routing is intent classification, which a 0.5B
// handles fine, and the small download keeps the one-click path fast.
const TINY_MODEL_ID = "Qwen2.5-0.5B-Instruct-q4f16_1-MLC";
const MODELS = [
  { id: TINY_MODEL_ID, label: "Qwen2.5 0.5B (tiny, fastest · default)" },
  { id: "Qwen2.5-1.5B-Instruct-q4f16_1-MLC", label: "Qwen2.5 1.5B (small, ~1 GB)" },
  { id: "Llama-3.2-3B-Instruct-q4f16_1-MLC", label: "Llama 3.2 3B (better routing, ~2 GB)" },
];

// The one-click example: a single-step, read-only macro with an unambiguous
// intent — most reliable to route with the tiny model, and fast to execute.
const EXAMPLE = { repo: "expressjs/express", task: "Summarize the open issues" };

// The "full loop" starts the library with TWO macros and treats triage as the
// NOVEL task (no macro yet) that gets authored from its recorded trajectory,
// then served live on a real repo.
const STARTER_MACROS = ["summarize_open_issues", "repo_health"];
const NOVEL_SPEC = AUTHORED_MACROS.find((s) => s.name === "triage_newest_pull")!;
const NOVEL_REPEAT = { repo: "vitejs/vite", task: "Triage the newest open pull request" };
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Preset repos are chosen so each task actually has data to act on (e.g. the
// triage repo reliably has open PRs); verified live against the GitHub API.
const PRESETS = [
  { label: "vitejs/vite", repo: "vitejs/vite", task: "Triage the newest open pull request" },
  { label: "expressjs/express", repo: "expressjs/express", task: "Summarize the open issues" },
  { label: "sindresorhus/got", repo: "sindresorhus/got", task: "Give me the repo overview" },
];

const $ = (id: string) => document.getElementById(id)!;

let session = new Session("browser");
let env: RouteEnv | null = null;
let weak: WebLLMAdapter | null = null;

function esc(s: unknown): string {
  return String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
}

function parseRepo(input: string): Repo | null {
  const t = input.trim().replace(/\.git$/, "");
  const url = t.match(/github\.com\/([^/\s]+)\/([^/\s?#]+)/i);
  if (url?.[1] && url[2]) return { owner: url[1], repo: url[2] };
  const slug = t.match(/^([^/\s]+)\/([^/\s]+)$/);
  if (slug?.[1] && slug[2]) return { owner: slug[1], repo: slug[2] };
  return null;
}

function renderState(): void {
  const s = session.snapshot();
  $("grad-pct").textContent = s.graduation + "%";
  ($("grad-fill") as HTMLElement).style.width = s.graduation + "%";
  $("grad-sub").textContent = `${s.servedByMacro} of ${s.total} requests served locally by a macro`;
  $("macro-count").textContent = String(s.macros.length);
  $("macros").innerHTML = s.macros
    .map((m) => {
      const steps = m.trajectory
        .map((t) => `<li><b>${esc(t.tool)}</b>(${esc(JSON.stringify(t.args))})</li>`)
        .join("");
      return `
      <div class="macro-card">
        <div class="mc-top">
          <span class="mc-name">${esc(m.name)}()</span>
          <span class="chip ${m.trust}">${m.trust === "candidate" ? "candidate" : "trusted"}</span>
        </div>
        <div class="mc-intent">${esc(m.intent)}</div>
        <div class="mc-meta">
          <span class="chip">params: ${m.params.map(esc).join(", ") || "—"}</span>
        </div>
        <details class="prov">
          <summary>how this was made (design time)</summary>
          <p class="prov-note">${esc(m.authoredNote)}</p>
          <ol class="steps">${steps}</ol>
        </details>
      </div>`;
    })
    .join("");
}

function renderRecord(rec: RequestRecord): void {
  const cls = rec.servedBy === "macro" ? "macro" : "unavailable";
  const pill =
    rec.servedBy === "macro"
      ? rec.costLabel
      : rec.servedBy === "errored"
        ? `routed to ${rec.macro} · couldn't complete`
        : "no macro — author one design-time";
  const steps = rec.steps.map((s) => `<li><b>${esc(s.tool)}</b>(${esc(JSON.stringify(s.args))})</li>`).join("");
  const div = document.createElement("div");
  div.className = "rec";
  div.innerHTML = `
    <div class="rec-head">
      <div><div class="rec-task">${esc(rec.task)}</div><div class="rec-repo">${esc(rec.repo)}</div></div>
      <span class="pill ${cls}">${esc(pill)}</span>
    </div>
    <div class="rec-body">
      <p class="rec-summary">${esc(rec.summary)}</p>
      ${steps ? `<ul class="steps">${steps}</ul>` : ""}
      <div class="cost">${esc(rec.costLabel)}</div>
    </div>`;
  const log = $("log");
  if (log.querySelector(".empty")) log.innerHTML = "";
  log.prepend(div);
}

function setModelStatus(text: string, kind: "idle" | "loading" | "ready" | "error" = "idle"): void {
  const el = $("model-status");
  el.textContent = text;
  el.className = "model-status " + kind;
}

async function loadModel(): Promise<void> {
  if (!WebLLMAdapter.webgpuAvailable()) {
    setModelStatus(
      "No WebGPU in this browser — the in-browser model can't run here. Try recent Chrome/Edge. (We never fall back to a cloud model.)",
      "error",
    );
    return;
  }
  const modelId = ($("model-select") as HTMLSelectElement).value;
  weak = new WebLLMAdapter({ model: modelId });
  ($("load-model") as HTMLButtonElement).disabled = true;
  setModelStatus("Downloading + compiling model (first time only)…", "loading");
  try {
    await weak.init((p) => setModelStatus(`${p.text} (${Math.round(p.progress * 100)}%)`, "loading"));
    env = {
      weak,
      github: new GitHubClient(),
      macroCostLabel: "served by macro · in-browser model · $0",
      mode: "live",
    };
    setModelStatus(`Ready — ${modelId} running locally on your GPU.`, "ready");
    ($("run") as HTMLButtonElement).disabled = false;
  } catch (e) {
    setModelStatus("Load failed: " + (e instanceof Error ? e.message : String(e)), "error");
    ($("load-model") as HTMLButtonElement).disabled = false;
  }
}

/**
 * One-click fast path: load the tiny default model, then run the example task
 * so a cold visitor watches a macro serve locally + graduation tick — no
 * typing. Honest: if there's no WebGPU we say so and do NOT fake routing; if
 * the model doesn't confidently match, handleTask reports it unserved.
 */
async function loadAndRunExample(): Promise<void> {
  if (!WebLLMAdapter.webgpuAvailable()) {
    setModelStatus(
      "No WebGPU in this browser — the macro library is real, but live routing needs Chrome/Edge with WebGPU. (We never fall back to a cloud model.)",
      "error",
    );
    return;
  }
  const btn = $("example") as HTMLButtonElement;
  btn.disabled = true;
  const original = btn.textContent;
  try {
    if (!env) {
      ($("model-select") as HTMLSelectElement).value = TINY_MODEL_ID;
      btn.textContent = "Loading tiny model…";
      await loadModel();
    }
    if (!env) return; // load failed; loadModel already surfaced the error
    ($("repo") as HTMLInputElement).value = EXAMPLE.repo;
    ($("task") as HTMLInputElement).value = EXAMPLE.task;
    btn.textContent = "Running the example…";
    await run();
  } finally {
    btn.textContent = original;
    btn.disabled = false;
  }
}

/** Prepend a styled entry to the activity log. Returns the node. */
function logEntry(html: string, cls = ""): HTMLElement {
  const log = $("log");
  if (log.querySelector(".empty")) log.innerHTML = "";
  const div = document.createElement("div");
  div.className = "rec " + cls;
  div.innerHTML = html;
  log.prepend(div);
  return div;
}

/**
 * The full pattern, one click: novel task → (recorded) strong-model authoring
 * → by-product macro appears → live weak model serves the repeat → graduation
 * climbs. Honesty: the authoring trajectory is REPLAYED from a recording and
 * is labelled as such; only the repeat is a live model call.
 */
async function seeFullLoop(): Promise<void> {
  if (!WebLLMAdapter.webgpuAvailable()) {
    setModelStatus(
      "No WebGPU in this browser — the macro library is real, but live routing needs Chrome/Edge with WebGPU. (We never fall back to a cloud model.)",
      "error",
    );
    return;
  }
  const btn = $("full-loop") as HTMLButtonElement;
  const orig = btn.textContent;
  btn.disabled = true;
  ($("example") as HTMLButtonElement).disabled = true;
  try {
    // Fresh demo session: drop to the 2 starter macros so the visitor can
    // watch the third (triage) get authored. (Cold state has all 3 so manual
    // presets always route; this guided tour rewinds to show the by-product.)
    session = new Session("browser");
    loadAuthoredMacros(session, STARTER_MACROS);
    $("log").innerHTML = "";
    renderState();
    logEntry(
      `<div class="rec-body"><p class="rec-summary">Starting a fresh demo session — 2 starter macros loaded; “${esc(NOVEL_SPEC.name)}” is the novel task with no macro yet.</p></div>`,
    );
    await delay(700);

    if (!env) {
      ($("model-select") as HTMLSelectElement).value = TINY_MODEL_ID;
      btn.textContent = "Loading tiny model…";
      await loadModel();
    }
    if (!env) return;
    btn.textContent = "Running the full loop…";

    // 1. Novel task — nothing in the library covers it.
    logEntry(
      `<div class="rec-head"><div><div class="rec-task">Novel task: ${esc(NOVEL_REPEAT.task)}</div>` +
        `<div class="rec-repo">no macro for this yet</div></div><span class="pill claude">novel</span></div>` +
        `<div class="rec-body"><p class="rec-summary">No bundled macro covers this. First, the strong model does the work — replayed here from a recording.</p></div>`,
    );
    await delay(900);

    // 2. Replay the recorded strong-model authoring trajectory, step by step.
    if (!session.cardFor(NOVEL_SPEC.name)) {
      for (let i = 0; i < NOVEL_SPEC.trajectory.length; i++) {
        const step = NOVEL_SPEC.trajectory[i]!;
        logEntry(
          `<div class="auth-step"><span class="auth-tag">recorded · design-time authoring</span>` +
            ` step ${i + 1}/${NOVEL_SPEC.trajectory.length} &middot; <b>${esc(step.tool)}</b>(${esc(JSON.stringify(step.args))})</div>`,
          "authoring",
        );
        await delay(750);
      }
      // 3. Distill the recorded trajectory into a macro (the by-product moment).
      const card = authorMacro(session, NOVEL_SPEC);
      renderState(); // card animates in; count 2 → 3
      logEntry(
        `<div class="rec-head"><div><div class="rec-task">By-product created: ${esc(card.name)}()</div>` +
          `<div class="rec-repo">distilled from the recorded run</div></div><span class="pill macro">new macro</span></div>` +
          `<div class="rec-body"><p class="rec-summary">That work was distilled into a reusable macro and added to your library (now ${session.cards.length}). Free — a by-product of work the strong model already did.</p></div>`,
        "authoring",
      );
      await delay(1000);
    } else {
      logEntry(`<div class="rec-body"><p class="rec-summary">"${esc(NOVEL_SPEC.name)}" is already in your library — running the repeat.</p></div>`);
    }

    // 4. Serve the REPEAT live — real weak model + real GitHub. Graduation ticks.
    logEntry(`<div class="rec-body"><p class="rec-summary">Now a <b>tiny local model</b> handles the same task again — routed locally, $0.</p></div>`);
    ($("repo") as HTMLInputElement).value = NOVEL_REPEAT.repo;
    ($("task") as HTMLInputElement).value = NOVEL_REPEAT.task;
    await run();
  } finally {
    btn.textContent = orig;
    btn.disabled = false;
    ($("example") as HTMLButtonElement).disabled = false;
  }
}

async function run(): Promise<void> {
  const repo = parseRepo(($("repo") as HTMLInputElement).value);
  const task = ($("task") as HTMLInputElement).value.trim();
  if (!repo) return alert("Enter a public repo as owner/repo or a github.com URL.");
  if (!task) return alert("Describe a maintainer task.");
  if (!env) {
    setModelStatus("Load the in-browser model first ▴", "idle");
    return;
  }
  const btn = $("run") as HTMLButtonElement;
  btn.disabled = true;
  btn.textContent = "Routing locally…";
  try {
    const rec = await handleTask(session, env, repo, task);
    renderRecord(rec);
    renderState();
  } catch (e) {
    alert("Error: " + (e instanceof Error ? e.message : String(e)));
  } finally {
    btn.disabled = false;
    btn.textContent = "Run task ▸";
  }
}

function reset(): void {
  session = new Session("browser");
  loadAuthoredMacros(session); // all three — every preset routes immediately
  $("log").innerHTML = '<p class="empty">Session reset. Hit “See the full loop”, or run a task below.</p>';
  renderState();
}

function wire(): void {
  const sel = $("model-select") as HTMLSelectElement;
  MODELS.forEach((m) => {
    const o = document.createElement("option");
    o.value = m.id;
    o.textContent = m.label;
    sel.appendChild(o);
  });

  const pr = $("presets");
  PRESETS.forEach((p) => {
    const b = document.createElement("button");
    b.textContent = p.label;
    b.onclick = () => {
      ($("repo") as HTMLInputElement).value = p.repo;
      ($("task") as HTMLInputElement).value = p.task;
    };
    pr.appendChild(b);
  });
  if (PRESETS[0]) {
    ($("repo") as HTMLInputElement).value = PRESETS[0].repo;
    ($("task") as HTMLInputElement).value = PRESETS[0].task;
  }

  ($("full-loop") as HTMLButtonElement).onclick = seeFullLoop;
  ($("example") as HTMLButtonElement).onclick = loadAndRunExample;
  ($("load-model") as HTMLButtonElement).onclick = loadModel;
  ($("run") as HTMLButtonElement).onclick = run;
  ($("reset") as HTMLButtonElement).onclick = reset;
  ($("task") as HTMLInputElement).addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") run();
  });

  if (!WebLLMAdapter.webgpuAvailable()) {
    setModelStatus("Heads up: this browser has no WebGPU. The macro library below is real, but live routing needs Chrome/Edge with WebGPU.", "error");
    ($("full-loop") as HTMLButtonElement).disabled = true;
    ($("example") as HTMLButtonElement).disabled = true;
  } else {
    setModelStatus("Tip: hit “▶ See the full loop” above — or pick a model and write your own task.", "idle");
  }

  // Cold state: all three macros loaded, so every preset routes immediately
  // (never "no macro" on a canonical task). "See the full loop" resets to two
  // starter macros and authors the third live, to show the by-product moment.
  loadAuthoredMacros(session);
  renderState();
}

wire();
